<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.report.label', 2), to: '/reports' },
      { label: $t('ui.entities.user.role.callAgent', 2) },
      { label: 'KPI', to: '/reports/call-agents/kpis' }
    ]"
  >
    <template #right>
      <div class="flex items-center gap-2">
        <Select
          v-model="callCenterSelected"
          :options="callCentersAllowed"
          icon="headset"
          value-key="id"
          label-key="name"
          :placeholder="`Select a ${$t('ui.entities.departments.callCentre')}`"
          class="min-w-56"
          :readonly="callCentersAllowed.length == 1"
          @select="updateCallCenterSelected"
        ></Select>

        <DatePicker
          v-model="dateRange"
          mode="range"
          :presets="dateRangePresets"
        />
        <div>
          <Button
            label="Update"
            :is-loading="isLoading"
            @click="onFetchData"
          />
        </div>
      </div>
    </template>
  </PageHeader>
  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      export-filename="Agent KPIs"
      :value="agentKpis"
      removable-sort
      :rows="100"
      data-key="userId"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :rows-per-page-options="[30, 50]"
      state-key="dt-agent-kpis"
      state-storage="session"
      :loading="isLoading"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
    >
      <template #empty>
        <Empty
          v-if="!isLoading"
          :title="callCenterSelected ? 'No data' : `Select a ${$t('ui.entities.departments.callCentre')}`"
        />
      </template>

      <template #header>
        <div class="flex justify-between">
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="filters['global'].value"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>
        </div>
        <div class="flex justify-between mt-4">
          <span>
            <Button
              label="CSV"
              type="button"
              severity="secondary"
              outlined
              icon="file-excel"
              @click="exportCSV($event)"
            />
          </span>
        </div>
      </template>
      <Column
        field="userId"
        header="Agent"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          {{ data.userName }}
        </template>
        <template #filter="{ filterModel }">
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="usersList"
            searchable
            mode="multiple"
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="Any"
            class="min-w-52"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
      </Column>
      <Column
        field="userName"
        hidden
      ></Column>
      <Column
        field="stats.kpi.productivity"
        header="Productivity"
        :sortable="true"
      >
        <template #body="{ data }">
          <Chip
            v-if="data.stats?.kpi?.productivity !== undefined"
            :severity="getRagSeverity(Math.round(data.stats.kpi.productivity * 100), 95, 95)"
          >
            {{ formatNumber(data.stats.kpi.productivity * 100, 'percent') }}%
          </Chip>
          <span v-else>-</span>
        </template>
      </Column>
      <Column
        field="stats.kpi.answerRate"
        header="Answer Rate"
        :sortable="true"
      >
        <template #body="{ data }">
          <Chip
            v-if="data.stats?.kpi?.answerRate !== undefined"
            :severity="getRagSeverity(Math.round(data.stats.kpi.answerRate * 100), 29, 29)"
          >
            {{ formatNumber(data.stats.kpi.answerRate * 100, 'percent') }}%
          </Chip>
          <span v-else>-</span>
        </template>
      </Column>
      <Column
        field="stats.kpi.opportunityRate"
        header="Opportunity Rate"
        :sortable="true"
      >
        <template #body="{ data }">
          <Chip
            v-if="data.stats?.kpi?.opportunityRate !== undefined"
            :severity="getRagSeverity(Math.round(data.stats.kpi.opportunityRate * 100), 62, 62)"
          >
            {{ formatNumber(data.stats.kpi.opportunityRate * 100, 'percent') }}%
          </Chip>
          <span v-else>-</span>
        </template>
      </Column>
      <Column
        field="stats.kpi.conversionRate"
        header="Conversion Rate"
        :sortable="true"
      >
        <template #body="{ data }">
          <Chip
            v-if="data.stats?.kpi?.conversionRate !== undefined"
            :severity="getRagSeverity(Math.round(data.stats.kpi.conversionRate * 100), 95, 95)"
          >
            {{ formatNumber(data.stats.kpi.conversionRate * 100, 'percent') }}%
          </Chip>
          <span v-else>-</span>
        </template>
      </Column>
      <Column
        field="stats.kpi.appVsCallConversion"
        header="App vs Call Conversion"
        :sortable="true"
      >
        <template #body="{ data }">
          <Chip
            v-if="data.stats?.kpi?.appVsCallConversion !== undefined"
            :severity="getRagSeverity(Math.round(data.stats.kpi.appVsCallConversion * 100), 95, 95)"
          >
            {{ formatNumber(data.stats.kpi.appVsCallConversion * 100, 'percent') }}%
          </Chip>
          <span v-else>-</span>
        </template>
      </Column>
      <Column
        field="stats.kpi.totalKpisHit"
        header="Total KPIs Hit"
        :sortable="true"
      >
        <template #body="{ data }">
          <div
            v-if="data.stats?.kpi?.totalKpisHit !== undefined"
            :class="'text-' + getRagSeverity(Math.round(data.stats.kpi.totalKpisHit * 100), 70, 90)"
          >
            {{ formatNumber(data.stats.kpi.totalKpisHit * 100, 'percent') }}%
          </div>
          <span v-else>-</span>
        </template>
      </Column>
      <Column
        field="stats.kpi.totalScore"
        header="Total Score"
        :sortable="true"
      >
        <template #body="{ data }">
          <div
            v-if="data.stats?.kpi?.totalScore !== undefined"
            :class="'text-' + getRagSeverity(Math.round(data.stats.kpi.totalScore * 100), 70, 90)"
          >
            {{ formatNumber(data.stats.kpi.totalScore * 100, 'percent') }}%
          </div>
          <span v-else>-</span>
        </template>
      </Column>
    </DataTable>
  </Card>
</template>

<script setup>
import Card from '@/components/card/Card.vue'
import PageHeader from '@/components/page/PageHeader.vue'
import DatePicker from '@/components/date-picker/DatePicker.vue'
import Input from '@/components/forms/Input.vue'
import Button from '@/components/button/Button.vue'
import Chip from '@/components/chip/Chip.vue'
import Select from '@/components/forms/Select.vue'
import Empty from '@/components/empty/Empty.vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { FilterMatchMode } from '@primevue/core/api'
import { formatNumber } from '@/utilities/number'

import dayjs from 'dayjs'

import useApiRequest from '@/composables/useApiRequest'
// stores
import { useCallCenterStore } from '@/stores/CallCenterStore.ts'

import { ref, computed } from 'vue'
import { alertToast } from '@/utilities/notification'
import { getRagSeverity } from '@/utilities'
import { datePresets } from '@/utilities/date-time'

const dt = ref()
const callCenterStore = useCallCenterStore()

const dateRange = ref({
  start: dayjs().startOf('month').format('YYYY-MM-DD'),
  end: dayjs().format('YYYY-MM-DD')
})
const dateRangePresets = [datePresets.thisWeek, datePresets.thisMonth, datePresets.lastMonth]

const statisticsApiRequest = useApiRequest()

const callCentersAllowed = callCenterStore.callCenters
const callCenterSelected = ref(null)

const agentKpis = ref([])
const users = ref([])
const filters = ref([])

const isLoading = statisticsApiRequest.isLoading

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    userId: { value: null, matchMode: FilterMatchMode.IN }
  }
}
initFilters()

function clearFilters() {
  initFilters()
}

function fetchStatistics() {
  if (!callCenterSelected.value) {
    return
  }

  try {
    statisticsApiRequest
      .send({
        endpoint: '/v1/reports/call-agents/statistics',
        params: {
          callCenterId: callCenterSelected.value,
          start: dayjs(dateRange.value.start).format('YYYY-MM-DD'),
          end: dayjs(dateRange.value.end).format('YYYY-MM-DD')
        }
      })
      .then(response => {
        if (response.success) {
          // Ensure data is always an array
          agentKpis.value = Array.isArray(response.data) ? response.data : []
        } else {
          agentKpis.value = []
          alertToast('Failed to fetch data', response.message || 'Unknown error', 'error')
        }
      })
      .catch(err => {
        agentKpis.value = []
        alertToast('Failed to fetch data', err.message || 'Unknown error', 'error')
      })
  } catch (err) {
    agentKpis.value = []
    alertToast('Failed to fetch data', err.message || 'Unknown error', 'error')
  }
}

if (!localStorage.getItem('report-filter-call-center-selected')) {
  if (callCentersAllowed.length > 0) {
    callCenterSelected.value = callCentersAllowed[0].id
  }
} else {
  callCenterSelected.value = parseInt(localStorage.getItem('report-filter-call-center-selected'))
}

// Only fetch if callCenterSelected is set
if (callCenterSelected.value) {
  fetchStatistics()
}

function onFetchData() {
  if (dateRange.value.start && dateRange.value.end && callCenterSelected.value) {
    fetchStatistics()
  }
}

function updateCallCenterSelected() {
  localStorage.setItem('report-filter-call-center-selected', callCenterSelected.value)
}

// Users List
const usersApiRequest = useApiRequest()
usersApiRequest.send({ endpoint: '/v1/users?userType=retain' }).then(response => {
  if (response.http.status == 200) {
    users.value = response.data
  }
})

const usersList = computed(() => {
  // if no users, return empty array
  if (!users.value) return []

  // filter out users who are not featured in the agentKpi list
  return users.value
    .filter(user => agentKpis.value.find(u => u.userId == user.id))
    .map(u => {
      return {
        value: u.id,
        label: u.fullName,
        profilePicUrl: u.profilePicUrl,
        initials: u.initials
      }
    })
})

const exportCSV = () => {
  dt.value.exportCSV()
}
</script>

<style></style>
